import React from 'react';
import { Create, useForm } from '@refinedev/antd';
import { Form, Input, Select, Switch, InputNumber, Card, Row, Col, Typography } from 'antd';
import { IResourceComponentsProps } from '@refinedev/core';
import { ConfigVersionSelect } from '@/components/ConfigVersionSelect';
import { iosPriceForSave, iosPricePairRule } from './iosPrice';

export const ProductCreate: React.FC<IResourceComponentsProps> = () => {
  const { formProps, saveButtonProps } = useForm();

  // Blank iOS prices go out as null, which also clears a saved iOS price.
  const handleFinish = (values: Record<string, unknown>) =>
    formProps.onFinish?.({
      ...values,
      iosPrice: iosPriceForSave(values.iosPrice as Parameters<typeof iosPriceForSave>[0]),
    });

  return (
    <Create saveButtonProps={saveButtonProps}>
      <Form {...formProps} layout="vertical" onFinish={handleFinish}>
        <Form.Item
          label="Config Version"
          name="configVersionId"
          extra="Leave empty to apply to every build. Pick a config version to limit this to the app versions mapped to it."
        >
          <ConfigVersionSelect style={{ width: '100%' }} />
        </Form.Item>
        <Card title="Basic Info" size="small" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item label="Name" name="name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                label="Store Product ID"
                name="storeProductId"
                rules={[{ required: true }]}
              >
                <Input placeholder="e.g. coinpackwcc4" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item label="Description" name="description">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={6}>
              <Form.Item label="Type" name="type" rules={[{ required: true }]}>
                <Select
                  options={[
                    { label: 'Consumable', value: 'consumable' },
                    { label: 'Non-Consumable', value: 'non_consumable' },
                    { label: 'Subscription', value: 'subscription' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item label="Category" name="category" rules={[{ required: true }]}>
                <Select
                  options={[
                    { label: 'Coins', value: 'coins' },
                    { label: 'Gems', value: 'gems' },
                    { label: 'Match Tokens', value: 'match_tokens' },
                    { label: 'Evo Tokens', value: 'evo_tokens' },
                    { label: 'Cards', value: 'cards' },
                    { label: 'Bundle', value: 'bundle' },
                    { label: 'Starter Pack', value: 'starter_pack' },
                    { label: 'Special Offer', value: 'special_offer' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={4}>
              <Form.Item label="Sort Order" name="sortOrder" initialValue={0}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={4}>
              <Form.Item label="Active" name="isActive" valuePropName="checked" initialValue={true}>
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={12} sm={2}>
              <Form.Item
                label="Featured"
                name="isFeatured"
                valuePropName="checked"
                initialValue={false}
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={12} sm={2}>
              <Form.Item
                label="Best Value"
                name="bestValue"
                valuePropName="checked"
                initialValue={false}
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title="Items (what the player receives)" size="small" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={12} sm={4}>
              <Form.Item label="Coins" name={['items', 'coins']}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={4}>
              <Form.Item label="Match Tokens" name={['items', 'matchTokens']}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={4}>
              <Form.Item label="Evo Tokens" name={['items', 'evoTokens']}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={4}>
              <Form.Item label="Gems" name={['items', 'gems']}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={4}>
              <Form.Item label="Cards" name={['items', 'cards']}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={4}>
              <Form.Item label="Card Rarity" name={['items', 'cardRarity']}>
                <Select
                  allowClear
                  options={[
                    { label: 'Rookie (0)', value: 0 },
                    { label: 'Semi-Pro (1)', value: 1 },
                    { label: 'Pro (2)', value: 2 },
                    { label: 'Legendary (3)', value: 3 },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title="Pricing" size="small" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={12} sm={6}>
              <Form.Item label="USD Price" name={['price', 'usd']}>
                <InputNumber min={0} step={0.01} prefix="$" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={6}>
              <Form.Item label="INR Price" name={['price', 'inr']}>
                <InputNumber min={0} prefix="₹" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={12} sm={6}>
              <Form.Item
                label="iOS USD Price"
                name={['iosPrice', 'usd']}
                dependencies={[['iosPrice', 'inr']]}
                rules={[iosPricePairRule('inr')]}
              >
                <InputNumber min={0} step={0.01} prefix="$" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={6}>
              <Form.Item
                label="iOS INR Price"
                name={['iosPrice', 'inr']}
                dependencies={[['iosPrice', 'usd']]}
                rules={[iosPricePairRule('usd')]}
              >
                <InputNumber min={0} precision={0} prefix="₹" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Typography.Text type="secondary">
                The App Store price. Leave both blank for the same price as Android. Players see the
                store's own price in the app; this is used for revenue and the catalogue.
              </Typography.Text>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={12} sm={6}>
              <Form.Item label="In-Game: Coins" name={['inGamePrice', 'coins']}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={6}>
              <Form.Item label="In-Game: Match Tokens" name={['inGamePrice', 'matchTokens']}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={6}>
              <Form.Item label="In-Game: Evo Tokens" name={['inGamePrice', 'evoTokens']}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={6}>
              <Form.Item label="In-Game: Premium" name={['inGamePrice', 'premiumCurrency']}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        </Card>
      </Form>
    </Create>
  );
};
